/* eslint-disable @typescript-eslint/no-var-requires */
import React, { useState, useCallback } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

// react-easy-crop uses .d.mts which node moduleResolution doesn't resolve; use require
const Cropper = require('react-easy-crop').default as React.ComponentType<any>;

interface Area { x: number; y: number; width: number; height: number; }
interface Point { x: number; y: number; }

// ── Canvas crop helper ────────────────────────────────────────
function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.src = url;
  });
}

export async function getCroppedFile(imageSrc: string, pixelCrop: Area, fileName: string, format: 'jpeg' | 'png' = 'jpeg'): Promise<File> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d')!;
  if (format === 'png') {
    // keep transparent pixels — do NOT fill background
  } else {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height,
  );
  const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
  const outName = fileName.replace(/\.[^.]+$/, '') + (format === 'png' ? '.png' : '.jpg');
  return new Promise(resolve =>
    canvas.toBlob(blob => resolve(new File([blob!], outName, { type: mimeType })), mimeType, format === 'jpeg' ? 0.92 : undefined),
  );
}

// ── Component ─────────────────────────────────────────────────
interface CropModalProps {
  /** Object URL of the selected image (from URL.createObjectURL) */
  imageSrc: string;
  /** Original file name, used for the output File name */
  fileName: string;
  /** Width / Height. 1 = square, 16/9 = widescreen. Omit for freestyle (free resize). */
  aspect?: number;
  /** Modal title */
  title: string;
  /** 'round' for logo circles, 'rect' for all others */
  cropShape?: 'rect' | 'round';
  /** Output image format. 'png' preserves transparency. Defaults to 'jpeg'. */
  format?: 'jpeg' | 'png';
  onDone: (file: File, previewUrl: string) => void;
  onClose: () => void;
}

export default function CropModal({
  imageSrc, fileName, aspect, title, cropShape = 'rect', format = 'jpeg', onDone, onClose,
}: CropModalProps) {
  const [crop, setCrop]     = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom]     = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [applying, setApplying] = useState(false);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedArea(pixels);
  }, []);

  const handleApply = async () => {
    if (!croppedArea) return;
    setApplying(true);
    try {
      const file = await getCroppedFile(imageSrc, croppedArea, fileName, format);
      const previewUrl = URL.createObjectURL(file);
      onDone(file, previewUrl);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal show onHide={onClose} centered size="lg" backdrop="static">
      <Modal.Header closeButton style={{ borderBottom: 'none', padding: '20px 24px 8px' }}>
        <Modal.Title style={{ fontWeight: 800, fontSize: '1.1rem' }}>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '0 24px 8px' }}>
        {/* Cropper area */}
        <div style={{ position: 'relative', width: '100%', height: 380, background: '#1e293b', borderRadius: '12px', overflow: 'hidden' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            {...(aspect !== undefined ? { aspect } : {})}
            cropShape={cropShape}
            showGrid={cropShape !== 'round'}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            style={{
              containerStyle: { borderRadius: '12px' },
              cropAreaStyle: { border: '2px solid rgba(255,255,255,0.85)' },
            }}
          />
        </div>

        {/* Zoom slider */}
        <div style={{ padding: '16px 4px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '0.78rem', color: '#94a3b8', minWidth: 32 }}>Zoom</span>
          <Form.Range
            min={1} max={3} step={0.05}
            value={zoom}
            onChange={e => setZoom(parseFloat(e.target.value))}
            style={{ flex: 1, accentColor: '#059669' }}
          />
          <span style={{ fontSize: '0.78rem', color: '#94a3b8', minWidth: 36 }}>{Math.round(zoom * 100)}%</span>
        </div>
        <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 6, marginBottom: 0 }}>
          Drag to reposition · Scroll or use slider to zoom
        </p>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: '1px solid #f1f5f9', padding: '14px 24px' }}>
        <Button variant="light" onClick={onClose} disabled={applying} style={{ borderRadius: '10px' }}>
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          disabled={applying}
          style={{ background: '#059669', border: 'none', borderRadius: '10px', fontWeight: 700, minWidth: 120 }}
        >
          {applying ? 'Applying…' : 'Apply Crop'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
